import {
  useState, useRef, useCallback, useEffect,
} from 'react';
import { toast } from 'react-toastify';
import { chatCompletion } from '../api/chat.api';
import {
  executeFlowStreaming,
  executeFlowEnhanced,
  recoverFlowExecution,
} from '../api/agenticFlow.api';
import { toDisplayString } from '../utils/flowResultUtils';
import { getLangGraphBackendUrl, getLangGraphFlowStatusUrl, getLangGraphSubmitFormUrl } from '../utils/langgraphConfig.js';

const messageType = {
  answer: 'answer',
  question: 'question',
  form: 'form',
  system: 'system',
};

export const useChatLogic = (username, onMessage, model = null, initialFlow = null) => {
  const [onRequest, setOnRequest] = useState(false);
  const [question, setQuestion] = useState('');
  const [file, setFile] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [typingInstance, setTypingInstance] = useState(null);
  const [typingPaused, setTypingPaused] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [activeFlowState, setActiveFlowState] = useState({
    isActive: false,
    flowId: null,
    executionId: null,
    sessionId: null,
    context: null,
    waitingForForm: false,
    completed: false,
    startTime: null,
    elapsedSeconds: 0,
    currentFormNodeId: null,
    // NEW: Streaming-specific state
    streamingConnection: null,
    progress: 0,
    currentNode: null,
    nodeStatuses: {},
    realTimeUpdates: [],
  });

  // Execution tracking state - Enhanced for streaming
  const [executionSteps, setExecutionSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [executionProgress, setExecutionProgress] = useState(0);

  const inputRef = useRef();
  const messagesEndRef = useRef();

  // Set initial flow when provided
  useEffect(() => {
    if (initialFlow) {
      setSelectedFlow(initialFlow);
    }
  }, [initialFlow]);

  // Flow state management functions
  const startNewFlow = useCallback((flowId, executionId, sessionId, context) => {
    setActiveFlowState((prev) => ({
      ...prev,
      isActive: true,
      flowId,
      executionId,
      sessionId,
      context,
      startTime: Date.now(),
      elapsedSeconds: 0,
      completed: false,
      waitingForForm: false,
    }));
  }, []);

  const continueExistingFlow = useCallback((executionId, sessionId, context) => {
    setActiveFlowState((prev) => ({
      ...prev,
      executionId,
      sessionId,
      context,
    }));
  }, []);

  const completeFlow = useCallback(() => {
    setActiveFlowState((prev) => ({
      ...prev,
      completed: true,
      isActive: false,
    }));
  }, []);

  // Main chat function
  const getAnswer = async () => {
    if (onRequest || !question.trim()) return;
    await getAnswerWithContent(question);
  };

  // Main chat function with content parameter
  const getAnswerWithContent = async (content) => {
    if (onRequest || !content.trim()) return;

    const questionMessage = {
      type: messageType.question,
      content,
      id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, questionMessage]);
    if (onMessage) {
      onMessage(questionMessage);
    }

    const currentQuestion = content;
    setQuestion('');
    stopListening();
    setOnRequest(true);

    try {
      if (selectedFlow) {
        // Flow execution logic
        const shouldContinueFlow = activeFlowState.isActive
                                 && activeFlowState.flowId === selectedFlow._id
                                 && activeFlowState.executionId
                                 && !activeFlowState.completed;

        if (shouldContinueFlow) {
          continueExistingFlow(activeFlowState.executionId, activeFlowState.sessionId, activeFlowState.context);
        } else {
          startNewFlow(selectedFlow._id, null, null, null);
        }

        // Get flow definition
        const flowDefResponse = await fetch(`/api/v1/flows/${selectedFlow._id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          },
        });

        if (!flowDefResponse.ok) {
          throw new Error(`Failed to fetch flow definition: ${flowDefResponse.status}`);
        }

        const flowData = await flowDefResponse.json();
        const flow = flowData.flow || flowData;

        // Execute flow
        const langgraphBackendUrl = getLangGraphBackendUrl();
        const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null;
        const res = await fetch(`${langgraphBackendUrl}/execute-flow`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            flow: {
              name: flow.name,
              description: flow.description,
              nodes: flow.nodes,
              edges: flow.edges,
              globalState: flow.globalState,
              settings: flow.settings,
            },
            input: { input: currentQuestion },
            sessionId: shouldContinueFlow ? activeFlowState.sessionId : null,
            context: shouldContinueFlow ? activeFlowState.context : null,
            user_id: username,
            tenant_id: tenantId || undefined,
          }),
        });

        const data = await res.json();
        const executionId = data.execution_id;

        if (shouldContinueFlow) {
          continueExistingFlow(executionId, data.sessionId, data.context);
        } else {
          startNewFlow(selectedFlow._id, executionId, data.sessionId, data.context);
        }

        // Check if the initial response indicates waiting for form input
        const initialResult = data.result;
        if (initialResult?.waiting_for_form_input || initialResult?.state?.waiting_for_form_input) {
          console.log('🔍 Flow is waiting for form input');

          // Extract form data from the response
          const formData = initialResult.form_fields_json || initialResult.state?.form_fields_json;
          const formNodeId = initialResult.form_node_waiting || initialResult.state?.form_node_waiting;

          if (formData && formNodeId) {
            console.log('🔍 Form data found:', { formData, formNodeId });

            // Parse the form fields JSON
            let parsedFormFields;
            try {
              parsedFormFields = typeof formData === 'string' ? JSON.parse(formData) : formData;
            } catch (parseError) {
              console.error('Error parsing form fields JSON:', parseError);
              parsedFormFields = [];
            }

            // Update flow state to indicate waiting for form
            setActiveFlowState((prev) => ({
              ...prev,
              waitingForForm: true,
              currentFormNodeId: formNodeId,
            }));

            // Create a form message
            const formMessage = {
              type: messageType.form,
              content: 'Please fill out the form below:',
              id: `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              timestamp: new Date(),
              formFields: parsedFormFields,
              formNodeId,
              executionId,
            };

            setMessages((prev) => [...prev, formMessage]);
            if (onMessage) {
              onMessage(formMessage);
            }

            // Start polling for flow status
            await pollForFlowStatus(executionId, langgraphBackendUrl);
          } else {
            console.log('🔍 No form data found in response');
            // Handle regular flow response
            handleFlowResponse(data);
          }
        } else {
          // Handle regular flow response
          handleFlowResponse(data);
        }
      } else {
        // Regular LLM chat
        const { response, err } = await chatCompletion({
          username,
          prompt: currentQuestion,
          model, // Use the model parameter
          conversationId: null,
          file,
        });

        if (response && !err) {
          const answerMessage = {
            type: messageType.answer,
            content: response.text || response.message || 'No response received',
            id: `a_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
          };

          setMessages((prev) => [...prev, answerMessage]);
          if (onMessage) {
            onMessage(answerMessage);
          }
        } else {
          throw new Error(err || 'No response received');
        }
      }
    } catch (error) {
      console.error('Chat request error:', error);
      const errorMessage = {
        type: messageType.answer,
        content: error.message || 'Sorry, there was an error processing your request.',
        id: `e_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
      if (onMessage) {
        onMessage(errorMessage);
      }
    } finally {
      setOnRequest(false);
      setFile(null);
    }
  };

  const startListening = () => {
    setIsListening(true);
    // Add speech recognition logic here if needed
  };

  const stopListening = () => {
    setIsListening(false);
  };

  const handleStopTyping = () => {
    if (typingInstance) {
      typingInstance.stop();
    }
  };

  // Poll for flow status after form submission
  const pollForFlowStatus = useCallback(async (executionId, langgraphBackendUrl) => {
    let pollCount = 0;
    const maxPolls = 60; // 60 * 2s = 2 minutes max
    const pollInterval = 2000; // 2 seconds

    while (pollCount < maxPolls) {
      try {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        pollCount++;

        console.log(`Polling for flow status (attempt ${pollCount}/${maxPolls})...`);

        const pollResponse = await fetch(getLangGraphFlowStatusUrl(executionId));
        const pollData = await pollResponse.json();

        // Update execution tracking
        if (pollData.execution_steps && Array.isArray(pollData.execution_steps)) {
          setExecutionSteps(pollData.execution_steps);

          // Find the current running step
          const runningStep = pollData.execution_steps.find((step) => step.status === 'running');
          if (runningStep) {
            setCurrentStep(runningStep);
          }

          // Calculate progress
          const totalSteps = pollData.execution_steps.length;
          const completedSteps = pollData.execution_steps.filter((step) => step.status === 'completed' || step.status === 'success').length;
          const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
          setExecutionProgress(progress);
        }

        // Update node telemetry if available
        if (pollData.nodeTelemetry && Array.isArray(pollData.nodeTelemetry)) {
          // Convert node telemetry to execution steps format
          const telemetrySteps = pollData.nodeTelemetry.map((node) => ({
            nodeId: node.nodeId,
            nodeType: node.nodeType,
            status: node.status,
            startedAt: node.startedAt,
            finishedAt: node.finishedAt,
            durationMs: node.durationMs,
            error: node.error,
            logs: node.logs || [],
            input_data: node.input_data,
            output_data: node.output_data,
          }));
          setExecutionSteps(telemetrySteps);
        }

        if (pollData.status === 'completed' || pollData.status === 'error') {
          console.log('Flow completed or errored, stopping polling');
          handleFlowResponse(pollData);
          break;
        } else if (pollData.status === 'paused' && pollData.waiting_for_form_input) {
          console.log('Flow paused waiting for form input');
          console.log('🔍 Checking if this is a new form or the same form:', {
            currentFormNodeId: activeFlowState.currentFormNodeId,
            newFormNodeId: pollData.form_node_waiting,
            isSameForm: activeFlowState.currentFormNodeId === pollData.form_node_waiting,
          });

          // Only handle the response if it's a different form node (new form)
          // or if we don't have a current form node ID (first form)
          if (!activeFlowState.currentFormNodeId || activeFlowState.currentFormNodeId !== pollData.form_node_waiting) {
            console.log('🔍 New form detected, handling response');
            handleFlowResponse(pollData);
            break; // Only break if we're displaying a new form
          } else {
            console.log('🔍 Same form detected, continuing to poll');
            // Continue polling for the same form - don't break
          }
        } else if (pollData.status === 'running') {
          console.log('Flow still running, continuing to poll');
          // Continue polling
        }
      } catch (error) {
        console.error('Polling error:', error);
        pollCount++;
      }
    }

    if (pollCount >= maxPolls) {
      console.log('Max polling attempts reached');
      const timeoutMessage = {
        type: messageType.answer,
        content: 'Flow execution timed out. Please try again.',
        id: `timeout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, timeoutMessage]);
      if (onMessage) {
        onMessage(timeoutMessage);
      }
    }
  }, [activeFlowState.currentFormNodeId, onMessage]);

  // NEW: Enhanced streaming execution for ChatWidget
  const executeFlowWithStreaming = useCallback(async (flowData, input) => {
    try {
      console.log('🔄 ChatWidget: Starting streaming execution for:', flowData.name);

      // Update flow state to indicate streaming start
      setActiveFlowState((prev) => ({
        ...prev,
        isActive: true,
        startTime: Date.now(),
        progress: 0,
        currentNode: null,
        nodeStatuses: {},
        realTimeUpdates: [],
      }));

      // Reset execution tracking
      setExecutionSteps([]);
      setCurrentStep(null);
      setExecutionProgress(0);

      const streamConnection = await executeFlowStreaming(
        flowData,
        input,
        // onUpdate callback - real-time updates
        (update) => {
          console.log('📡 ChatWidget streaming update:', update.type, update.node_id);

          // Update progress
          if (update.progress?.percentage !== undefined) {
            setExecutionProgress(update.progress.percentage);
            setActiveFlowState((prev) => ({
              ...prev,
              progress: update.progress.percentage,
              currentNode: update.node_id,
            }));
          }

          // Convert to execution steps format
          if (update.node_id) {
            const stepUpdate = {
              nodeId: update.node_id,
              nodeType: update.type,
              status: update.type === 'node_complete' ? 'completed'
                : update.type === 'node_error' ? 'error' : 'running',
              startedAt: update.timestamp,
              executionTimeMs: (update.execution_time || 0) * 1000,
              metadata: { streaming: true, data: update.data },
            };

            setExecutionSteps((prev) => {
              const existing = prev.find((step) => step.nodeId === update.node_id);
              return existing
                ? prev.map((step) => (step.nodeId === update.node_id ? { ...step, ...stepUpdate } : step))
                : [...prev, stepUpdate];
            });

            if (update.type !== 'node_complete') {
              setCurrentStep(stepUpdate);
            }
          }

          // Handle completion/error
          if (update.type === 'flow_complete') {
            setActiveFlowState((prev) => ({ ...prev, isActive: false, completed: true }));
            setCurrentStep(null);
            setExecutionProgress(100);

            const completionMessage = {
              type: messageType.answer,
              content: toDisplayString(update.data?.result ?? update.data?.ai_response ?? update.data, 'Flow completed successfully'),
              id: `stream_complete_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              timestamp: new Date(),
              metadata: { streaming: true, executionTime: update.execution_time },
            };

            setMessages((prev) => [...prev, completionMessage]);
            if (onMessage) onMessage(completionMessage);
          } else if (update.type === 'flow_error') {
            setActiveFlowState((prev) => ({ ...prev, isActive: false }));
            setCurrentStep(null);

            const errorMessage = {
              type: messageType.answer,
              content: `❌ Flow execution failed: ${update.error}`,
              id: `stream_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              timestamp: new Date(),
              metadata: { error: true, streaming: true },
            };

            setMessages((prev) => [...prev, errorMessage]);
            if (onMessage) onMessage(errorMessage);

            // Show recovery option
            setTimeout(() => {
              if (window.confirm('Flow execution failed. Would you like to try recovering it?')) {
                handleStreamingRecovery(update.execution_id);
              }
            }, 1000);
          }
        },
        // onError callback
        (error) => {
          console.error('❌ ChatWidget streaming error:', error);
          toast.error('Streaming failed. Falling back to polling...');

          // Fallback to traditional method
          setActiveFlowState((prev) => ({ ...prev, streamingConnection: null }));
        },
      );

      setActiveFlowState((prev) => ({ ...prev, streamingConnection }));
      return streamConnection;
    } catch (error) {
      console.error('ChatWidget streaming setup failed:', error);
      toast.error('Failed to start streaming execution');
      throw error;
    }
  }, [onMessage]);

  // Handle streaming recovery
  const handleStreamingRecovery = useCallback(async (executionId) => {
    try {
      toast.info('Attempting to recover flow execution...');
      const recoveryResult = await recoverFlowExecution(executionId);

      if (recoveryResult.success) {
        toast.success('Flow execution recovered successfully!');
        const recoveryMessage = {
          type: messageType.answer,
          content: '✅ Flow execution recovered and completed successfully.',
          id: `recovery_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
          metadata: { recovered: true },
        };

        setMessages((prev) => [...prev, recoveryMessage]);
        if (onMessage) onMessage(recoveryMessage);
      } else {
        toast.error(`Recovery failed: ${recoveryResult.error}`);
      }
    } catch (error) {
      console.error('Recovery error:', error);
      toast.error('Failed to recover flow execution');
    }
  }, [onMessage]);

  // Handle flow response
  const handleFlowResponse = useCallback((data) => {
    console.log('🔍 Handling flow response:', data);

    // Update execution tracking from response data
    if (data.execution_steps && Array.isArray(data.execution_steps)) {
      setExecutionSteps(data.execution_steps);
    }

    if (data.nodeTelemetry && Array.isArray(data.nodeTelemetry)) {
      const telemetrySteps = data.nodeTelemetry.map((node) => ({
        nodeId: node.nodeId,
        nodeType: node.nodeType,
        status: node.status,
        startedAt: node.startedAt,
        finishedAt: node.finishedAt,
        durationMs: node.durationMs,
        error: node.error,
        logs: node.logs || [],
        input_data: node.input_data,
        output_data: node.output_data,
      }));
      setExecutionSteps(telemetrySteps);
    }

    if (data.status === 'completed') {
      // Flow completed successfully
      const result = data.result || data;
      const responseText = toDisplayString(result, 'Flow completed successfully');

      // Create execution summary message
      let executionSummary = '';
      if (executionSteps.length > 0) {
        const completedSteps = executionSteps.filter((step) => step.status === 'completed' || step.status === 'success');
        const failedSteps = executionSteps.filter((step) => step.status === 'error');

        executionSummary = '\n\n**Execution Summary:**\n';
        executionSummary += `• Total Steps: ${executionSteps.length}\n`;
        executionSummary += `• Completed: ${completedSteps.length}\n`;
        executionSummary += `• Failed: ${failedSteps.length}\n`;

        if (executionSteps.length > 0) {
          executionSummary += '\n**Step Details:**\n';
          executionSteps.forEach((step, index) => {
            const status = step.status === 'completed' || step.status === 'success' ? '✅'
              : step.status === 'error' ? '❌'
                : step.status === 'running' ? '🔄' : '⏳';
            const duration = step.durationMs ? ` (${step.durationMs}ms)` : '';
            executionSummary += `${status} Step ${index + 1}: ${step.nodeId || step.nodeType}${duration}\n`;
          });
        }
      }

      const answerMessage = {
        type: messageType.answer,
        content: responseText + executionSummary,
        id: `a_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        executionSteps, // Include execution steps in message
      };

      setMessages((prev) => [...prev, answerMessage]);
      if (onMessage) {
        onMessage(answerMessage);
      }

      // Mark flow as completed
      completeFlow();
    } else if (data.status === 'error') {
      // Flow failed
      const errorText = data.error || data.message || 'Flow execution failed';

      // Add execution steps to error message if available
      let errorSummary = '';
      if (executionSteps.length > 0) {
        errorSummary = '\n\n**Execution Steps (before error):**\n';
        executionSteps.forEach((step, index) => {
          const status = step.status === 'completed' || step.status === 'success' ? '✅'
            : step.status === 'error' ? '❌'
              : step.status === 'running' ? '🔄' : '⏳';
          errorSummary += `${status} Step ${index + 1}: ${step.nodeId || step.nodeType}\n`;
        });
      }

      const errorMessage = {
        type: messageType.answer,
        content: errorText + errorSummary,
        id: `e_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        executionSteps, // Include execution steps in message
      };

      setMessages((prev) => [...prev, errorMessage]);
      if (onMessage) {
        onMessage(errorMessage);
      }

      // Mark flow as completed (failed)
      completeFlow();
    } else if (data.waiting_for_form_input || data.state?.waiting_for_form_input) {
      // Flow is waiting for form input
      const formData = data.form_fields_json || data.state?.form_fields_json;
      const formNodeId = data.form_node_waiting || data.state?.form_node_waiting;

      if (formData && formNodeId) {
        let parsedFormFields;
        try {
          parsedFormFields = typeof formData === 'string' ? JSON.parse(formData) : formData;
        } catch (parseError) {
          console.error('Error parsing form fields JSON:', parseError);
          parsedFormFields = [];
        }

        // Update flow state to indicate waiting for form
        setActiveFlowState((prev) => ({
          ...prev,
          waitingForForm: true,
          currentFormNodeId: formNodeId,
        }));

        // Create a form message
        const formMessage = {
          type: messageType.form,
          content: 'Please fill out the form below:',
          id: `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
          formFields: parsedFormFields,
          formNodeId,
          executionId: activeFlowState.executionId,
        };

        setMessages((prev) => [...prev, formMessage]);
        if (onMessage) {
          onMessage(formMessage);
        }
      }
    }
  }, [activeFlowState.executionId, activeFlowState.currentFormNodeId, onMessage, completeFlow, executionSteps]);

  const handleFormSubmission = useCallback(async (formData) => {
    if (!activeFlowState.executionId) return;

    try {
      const response = await fetch(getLangGraphSubmitFormUrl(activeFlowState.executionId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ form_data: formData }),
      });

      const result = await response.json();

      if (result.success) {
        // Update flow state to indicate form was submitted
        setActiveFlowState((prev) => ({
          ...prev,
          waitingForForm: false,
          currentFormNodeId: null,
        }));

        const formMessage = {
          type: messageType.system,
          content: 'Form submitted successfully. Processing...',
          id: `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, formMessage]);
        if (onMessage) {
          onMessage(formMessage);
        }

        // Start polling for flow status after form submission
        const langgraphBackendUrl = getLangGraphBackendUrl();
        await pollForFlowStatus(activeFlowState.executionId, langgraphBackendUrl);
      } else {
        throw new Error(result.error || 'Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form');

      const errorMessage = {
        type: messageType.system,
        content: `Form submission failed: ${error.message}`,
        id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
      if (onMessage) {
        onMessage(errorMessage);
      }
    }
  }, [activeFlowState.executionId, onMessage, pollForFlowStatus]);

  return {
    // State
    onRequest,
    question,
    setQuestion,
    file,
    setFile,
    isListening,
    messages,
    setMessages,
    selectedFlow,
    setSelectedFlow,
    activeFlowState,
    setActiveFlowState,

    // Execution tracking state
    executionSteps,
    setExecutionSteps,
    currentStep,
    setCurrentStep,
    executionProgress,
    setExecutionProgress,

    // Refs
    inputRef,
    messagesEndRef,

    // Functions
    getAnswer,
    getAnswerWithContent,
    startListening,
    stopListening,
    handleStopTyping,
    handleFormSubmission,
    startNewFlow,
    continueExistingFlow,
    completeFlow,
    pollForFlowStatus,
    handleFlowResponse,
    // NEW: Streaming functions
    executeFlowWithStreaming,
    handleStreamingRecovery,
  };
};
